// src/store/slices/poc/cloneQueueSlice.ts

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { FileMetadata } from '@/types/poc/helia';

export interface QueuedFile {
  sixId: string;
  cid: string;
  metadata: FileMetadata;
  size: number;
  priority: 'normal' | 'user-initiated';
  addedAt: number;
}

export interface CloningProgress {
  bytesLoaded: number;
  totalBytes: number;
}

export interface CloneQueueState {
  queue: QueuedFile[];
  currentlyCloning: QueuedFile | null;
  currentSixId: string | null;
  failedFiles: Record<string, string>;
  cloningProgress: CloningProgress | null;
}

const initialState: CloneQueueState = {
  queue: [],
  currentlyCloning: null,
  currentSixId: null,
  failedFiles: {},
  cloningProgress: null,
};

const cloneQueueSlice = createSlice({
  name: 'cloneQueue',
  initialState,
  reducers: {
    enqueueFile: (state, action: PayloadAction<Omit<QueuedFile, 'addedAt'>>) => {
      const { cid, priority } = action.payload;

      // If already queued, upgrade priority if needed
      const existing = state.queue.find(f => f.cid === cid);
      if (existing != null) {
        if (priority === 'user-initiated' && existing.priority !== 'user-initiated') {
          existing.priority = 'user-initiated';
        }
        return;
      }

      // Don't add if currently cloning this file
      if (state.currentlyCloning?.cid === cid) {
        return;
      }

      // Don't add if previously failed (user must retry explicitly)
      if (cid in state.failedFiles) {
        return;
      }

      state.queue.push({
        ...action.payload,
        addedAt: Date.now(),
      });
    },

    setCurrentSix: (state, action: PayloadAction<string | null>) => {
      state.currentSixId = action.payload;
    },

    startCloning: (state, action: PayloadAction<QueuedFile>) => {
      state.currentlyCloning = action.payload;
      state.cloningProgress = null;
      state.queue = state.queue.filter(f => f.cid !== action.payload.cid);
    },

    finishCloning: (state) => {
      state.currentlyCloning = null;
      state.cloningProgress = null;
    },

    updateCloningProgress: (state, action: PayloadAction<CloningProgress>) => {
      state.cloningProgress = action.payload;
    },

    interruptCloning: (state) => {
      if (state.currentlyCloning === null) return;
      state.queue.unshift(state.currentlyCloning);
      state.currentlyCloning = null;
      state.cloningProgress = null;
    },

    markFailed: (state, action: PayloadAction<{ cid: string; error: string }>) => {
      state.failedFiles[action.payload.cid] = action.payload.error;
      if (state.currentlyCloning?.cid === action.payload.cid) {
        state.currentlyCloning = null;
        state.cloningProgress = null;
      }
    },

    clearFailed: (state, action: PayloadAction<string>) => {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- Immer draft: removing a single failed entry by CID
      delete state.failedFiles[action.payload];
    },

    clearAllFailed: (state) => {
      state.failedFiles = {};
    },

    clearQueue: (state) => {
      state.queue = [];
      state.currentlyCloning = null;
    },

    removeFromQueue: (state, action: PayloadAction<string>) => {
      state.queue = state.queue.filter(f => f.cid !== action.payload);
    },
  },
});

export const {
  enqueueFile,
  setCurrentSix,
  startCloning,
  finishCloning,
  updateCloningProgress,
  interruptCloning,
  markFailed,
  clearFailed,
  clearAllFailed,
  clearQueue,
  removeFromQueue,
} = cloneQueueSlice.actions;

// Selectors
export const selectCloneQueue = (state: { cloneQueue: CloneQueueState }) =>
  state.cloneQueue.queue;

export const selectCurrentlyCloning = (state: { cloneQueue: CloneQueueState }) =>
  state.cloneQueue.currentlyCloning;

export const selectCurrentSixId = (state: { cloneQueue: CloneQueueState }) =>
  state.cloneQueue.currentSixId;

export const selectQueueLength = (state: { cloneQueue: CloneQueueState }) =>
  state.cloneQueue.queue.length;

export const selectFailedFiles = (state: { cloneQueue: CloneQueueState }) =>
  state.cloneQueue.failedFiles;

export const selectIsCloning = (state: { cloneQueue: CloneQueueState }) =>
  state.cloneQueue.currentlyCloning !== null;

export const selectCloningProgress = (state: { cloneQueue: CloneQueueState }) =>
  state.cloneQueue.cloningProgress;

export default cloneQueueSlice.reducer;
